<!--
    Field TypeSelect
    Used By: FieldItem, FieldModal.
    Purpose: Intercept changes and where appropriate confirm with user before applying the change.
-->

<template>
    <b-form-select
        :id="id"
        v-model="dataTypeName"
        :options="options"
        :disabled="disabled"
        :class="classNames"
        @change="onFieldTypeChange"
        />
</template>

<script>

    /* Utilities */
    import * as entityUtils from '@/core/utils/entityUtils';

    export default {

        name: 'FieldTypeSelect',

        props: {
            /** Current Field. */
            field: {
                type: Object,
                required: true,
            },
            /** Select control options. */
            options: {
                type: Array,
                required: true,
            },
            /** Flag to indicate whether the Select control should be disabled. */
            disabled: {
                type: Boolean,
                default: false,
            },
            /** Select control Id. */
            id: {
                type: String,
                default: '',
            },
            /** Additional classes to configure the Select control. */
            classNames: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                /* Value used by Select control. */
                dataTypeName: '',
            };
        },

        watch: {
            /** Whenever the Field's data type changes (externally to this control), copy the value to the control. */
            'field.dataTypeName': {
                handler(newVal, oldVal) {
                    //console.log(`FieldTypeSelect: field.dataTypeName changed. Old: ${oldVal}. New: ${newVal}.`);
                    if (this.dataTypeName !== newVal)
                        this.dataTypeName = newVal;
                },
                immediate: true,
                deep: true,
            },
        },

        methods: {
            /**
             * Called when the field type is changed.
             * @param {string} newVal - The new value of the field type.
             */
            onFieldTypeChange(newVal) {
                const dataTypeChangeMessage = entityUtils.getDataTypeChangeMessage(this.field, newVal);
                if (this.dataTypeName !== '' && newVal !== this.field.dataTypeName && dataTypeChangeMessage)
                {
                  /* Initialise message lines and convert to VNode list for Confirm modal. */
                  const messageLines = [
                    dataTypeChangeMessage,
                    'Are you sure that the field\'s data type should be changed?',
                  ];
                  const message = messageLines.map((msg) => this.$createElement('div', { class: 'confirm-message-line', domProps: { innerHTML: msg } }));
                  const confirmOptions = { title:'Field Data Type Change', okTitle: 'Yes', okVariant: 'warning', cancelTitle: 'No'/*, cancelVariant: 'warning'*/ };
                  this.$bvModal.msgBoxConfirm(message, confirmOptions)
                    .then((response) => {
                      /* Confirm response: 'Yes': true, 'No': false. */
                      if (response){
                        /* User response: 'Yes' - Update the Field's definition and invoke the 'change' event. */
                        this.assignDataTypeToField();
                      } else {
                        /* User response: 'No' - Revert the selection. */
                        this.dataTypeName = this.field.dataTypeName;
                        return;
                      }
                    });
                }
                else{
                    /* No confirmation required - Assign value and propogate event. */
                    this.assignDataTypeToField();
                }
            },

            /** If the values differ, copy the selected value to the Field and trigger 'Change' event. */
            assignDataTypeToField() {
                if (this.field.dataTypeName !== this.dataTypeName){
                    /* Assign the data type to the field. */
                    this.field.dataTypeName = this.dataTypeName;
                    /* Trigger component's 'Change' event. */
                    this.$emit('change', this.dataTypeName);
                }
            },
        },

        /** Before Mount - assign the Field's Data Type to the Select control. */
        beforeMount() {
            this.dataTypeName = this.field.dataTypeName;
        },

    };

</script>

<style lang="css" scoped>
.confirm-message-line {
    margin-bottom: 12px;
    padding: 0px;
}
</style>